import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { Divider, Stack } from "@mui/material";
import { FetchApiError, type ValidateLocalAccountResponse } from "@repo/api";
import { B2CPolicies, type B2CPolicy } from "@repo/logic";
import { BodyTextM, cs, HeadingL, HeadingXS } from "@repo/ui";
import rozcestnikImage from "@repo/ui/assets/images/rozcestnik.png";
import { Formik } from "formik";
import { navigate, type PageProps } from "gatsby";
import { type FC, useState } from "react";
import { object, string } from "yup";
import { useCookieConsents } from "../../context/CookieContext";
import { useValidateLocalAccount } from "../../hooks-mutations/useValidateLocalAccount";
import Attention from "../AttentionBlock";
import CookieDrawer from "../Drawer/CookieDrawer";
import CookieButton from "../Drawer/CookieDrawer/CookieButton";
import Form from "../Form/Form";
import TextInput from "../Form/Input/Text";
import FormSubmit from "../Form/Submit";
import { InternalButtonLink } from "../Link";
import { LoginButton } from "../LoginButton";
import { LoginButtonType } from "../LoginButton/loginEnums";
import { isLoggedInMsal } from "../LoginButton/useLogin";
import { loginErrorToText, loginRedirect } from "../LoginButton/useLogin.helpers";
import { useLoginError } from "../LoginButton/useLoginError";
import * as Styled from "./GuidepostSimplified.styled";

const GuidepostSimplified: FC<{
    pageProps: PageProps;
}> = ({ pageProps }) => {
    const { displayCookieForm, setDisplayCookieForm } = useCookieConsents();
    const msal = useMsal();
    const { mutateAsync: validateLocalAccount, isError } = useValidateLocalAccount();
    const { authority, loginError } = useLoginError();
    const loading = msal.inProgress !== InteractionStatus.None;
    const [isEmailFilled, setIsEmailFilled] = useState(false);
    const [filledEmail, setFilledEmail] = useState("");
    const customLoginError = pageProps.location.state?.customLoginError;

    return (
        <>
            <Styled.StyledOverview image={rozcestnikImage} alt="Přihlášení">
                <Styled.ContentBox>
                    <HeadingL>{cs.crossroad.head}</HeadingL>
                    {customLoginError && <Attention severity="error">{customLoginError}</Attention>}

                    {authority === undefined && loginError && (
                        <Attention severity="error" title={loginErrorToText[loginError.errorCode] ?? undefined}>
                            {loginError.errorMessage}
                        </Attention>
                    )}

                    <BodyTextM>{cs.crossroad.headText}</BodyTextM>

                    <LoginButton
                        journey={LoginButtonType.BankId}
                        sx={{ width: "100%", mt: 4, px: { xs: 1 }, flexWrap: "wrap" }}
                    />

                    {authority === B2CPolicies.BankIdLogin() && loginError && (
                        <Attention severity="error" title={loginErrorToText[loginError.errorCode] ?? undefined}>
                            {loginError.errorMessage}
                        </Attention>
                    )}
                    <Divider sx={{ my: 4 }}>
                        <Styled.DividerText variant="bodyTextM">{cs.crossroad.dividerText}</Styled.DividerText>
                    </Divider>
                    <HeadingXS marginBottom={3}>E-mail</HeadingXS>
                    {isError ? (
                        <Styled.ActionStack direction="row" justifyContent="space-between" spacing={5}>
                            <InternalButtonLink to="/prihlaseni/" variant="outlined" sx={{ width: "50%", py: "11px" }}>
                                <BodyTextM>Přihlášení</BodyTextM>
                            </InternalButtonLink>
                            <Styled.ActionButton
                                underline="none"
                                disableElevation
                                onClick={() => {
                                    navigate("/nova-registrace/", {
                                        state: { prefilledUsername: filledEmail },
                                    });
                                }}
                                variant="outlined"
                            >
                                <BodyTextM>Registrace</BodyTextM>
                            </Styled.ActionButton>
                        </Styled.ActionStack>
                    ) : (
                        <Formik
                            initialValues={{ email: "" }}
                            validationSchema={object().shape({
                                email: string().email().required(),
                            })}
                            onSubmit={async ({ email }, helpers) => {
                                const isLoggedIn = await isLoggedInMsal(msal);
                                if (isLoggedIn) {
                                    navigate("/smlouvy");
                                    return;
                                }

                                setFilledEmail(email);

                                try {
                                    const res = await validateLocalAccount(email);

                                    if (res.isAccountRegistered) {
                                        await loginRedirect(msal, LoginButtonType.LocalLogin, email);
                                    } else {
                                        await loginRedirect(msal, LoginButtonType.LocalRegistration, email);
                                    }
                                } catch (error) {
                                    if (error instanceof FetchApiError) {
                                        const err = error as FetchApiError<ValidateLocalAccountResponse>;

                                        if (err.errorResponse.statusCode !== 200) {
                                            helpers.setErrors({
                                                email: err.errorResponse.userMessage ?? "Nepodařilo se ověřit e-mail.",
                                            });
                                            return;
                                        }
                                    }

                                    helpers.setErrors({
                                        email: "Nepodařilo se ověřit e-mail.",
                                    });
                                }
                            }}
                        >
                            <Form spacing={2} showSubmitButton={false}>
                                <TextInput
                                    sx={{ mb: 4 }}
                                    updateFormikOnChange
                                    placeholder={cs.crossroad.emailPlaceholder}
                                    label=""
                                    name="email"
                                    onChangeCaptureEvent={(evt) => {
                                        setIsEmailFilled(Boolean(evt.target.value));
                                    }}
                                    inputProps={{
                                        inputMode: "email",
                                    }}
                                />
                                <FormSubmit
                                    loading={loading}
                                    text={{
                                        submitting: "...",
                                        label: cs.crossroad.emailButton,
                                    }}
                                    sx={{
                                        width: "100%",
                                        textAlign: "center",
                                        px: { xs: 1 },
                                    }}
                                />

                                {authority &&
                                    ([B2CPolicies.DirectLogin(), B2CPolicies.PrototypeLogin()] as B2CPolicy[]).includes(
                                        authority as B2CPolicy,
                                    ) &&
                                    loginError && (
                                        <Attention
                                            severity="error"
                                            title={loginErrorToText[loginError.errorCode] ?? undefined}
                                        >
                                            {loginError.errorMessage}
                                        </Attention>
                                    )}
                            </Form>
                        </Formik>
                    )}
                </Styled.ContentBox>
            </Styled.StyledOverview>
            <Styled.ProductStack spacing={5}>
                <HeadingL>{cs.crossroad.productHead}</HeadingL>
                <BodyTextM>
                    <Styled.ProductTextStack>{cs.crossroad.productText}</Styled.ProductTextStack>
                    <Styled.BulletListContainer>
                        <Styled.BulletListWrapper>
                            <Styled.BulletListPrimaryColumn>
                                <Styled.BulletList data-clear>
                                    <Styled.BulletItem>{cs.crossroad.productRisk}</Styled.BulletItem>
                                    <Styled.BulletItem>{cs.crossroad.productInvest}</Styled.BulletItem>
                                    <Styled.BulletItem>{cs.crossroad.productBenefit}</Styled.BulletItem>
                                </Styled.BulletList>
                            </Styled.BulletListPrimaryColumn>
                            <Styled.BulletListSecondaryColumn>
                                <Styled.BulletList data-clear>
                                    <Styled.BulletItem>{cs.crossroad.productBonus}</Styled.BulletItem>
                                    <Styled.BulletItem>{cs.crossroad.productCare}</Styled.BulletItem>
                                </Styled.BulletList>
                            </Styled.BulletListSecondaryColumn>
                        </Styled.BulletListWrapper>
                        <Styled.BulletListColumn>
                            <Styled.BulletListFull data-clear>
                                <li>{cs.crossroad.productAegon}</li>
                            </Styled.BulletListFull>
                        </Styled.BulletListColumn>
                    </Styled.BulletListContainer>
                </BodyTextM>

                <InternalButtonLink
                    to="https://mojenn20.nn.cz"
                    sx={{ width: "100%", textAlign: "center", px: { xs: 5 } }}
                    variant="outlined"
                    disabled={loading || isEmailFilled}
                >
                    {cs.crossroad.productButton}
                </InternalButtonLink>
            </Styled.ProductStack>

            <Stack>
                <CookieButton
                    onClick={() => {
                        setDisplayCookieForm(true);
                    }}
                />
                {displayCookieForm && <CookieDrawer floating />}
            </Stack>
        </>
    );
};

export default GuidepostSimplified;
